import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { SUBSCRIPTIONS_ENABLED } from '../../config/features.ts';
import './DailyLimitNotice.css';

/** Compact token count for the guest phrasing: 150000 → "150k". */
function fmtTokens(n: number): string {
  if (n >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  }
  if (n >= 1_000) {
    return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}k`;
  }
  return String(n);
}

interface DailyLimitNoticeProps {
  /** The daily prompt cap that was hit (e.g. 15). */
  limit: number;
  /** ISO-8601 instant the cap resets (the user's next local midnight). */
  resetAt: string;
  /** Guest console (tiers T8): the budget is OM TOKENS, not messages, and
   *  the upsell becomes the create-account CTA. */
  guest?: boolean;
}

/** Format a remaining duration as `m:ss`, or `h:mm:ss` past an hour. */
function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${ss}`;
  }
  return `${m}:${ss}`;
}

/**
 * Transcript notice shown when the user is over their daily prompt cap. Carries
 * a live countdown and a "recharge" progress bar to the reset instant (computed
 * client-side from `resetAt` — the runtime only sends the instant), plus a note
 * that subscriptions for higher limits are coming. Mirrors DocumentCard's
 * countdown idiom so the two transcript timers read the same.
 */
export default function DailyLimitNotice({ limit, resetAt, guest = false }: DailyLimitNoticeProps) {
  const resetMs = Date.parse(resetAt);
  const hasReset = Number.isFinite(resetMs);
  const [now, setNow] = useState(() => Date.now());
  // Anchor the bar to first paint so it recharges over the wait the user
  // actually sees, without assuming when the 24h window started.
  const startRef = useRef(now);

  const remaining = hasReset ? Math.max(0, resetMs - now) : null;
  const isReset = remaining !== null && remaining <= 0;

  // Tick once a second until the cap resets, then stop.
  useEffect(() => {
    if (!hasReset || isReset) {
      return;
    }
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [hasReset, isReset]);

  // Recharge fill: ~0% at first paint → 100% at reset.
  const span = hasReset ? resetMs - startRef.current : 0;
  const pct =
    remaining !== null && span > 0
      ? Math.max(0, Math.min(100, ((span - remaining) / span) * 100))
      : isReset
        ? 100
        : 0;

  // Absolute reset time in the *browser's* local zone (no explicit timeZone =>
  // host zone), with the zone abbreviation so the instant is unambiguous. The
  // countdown above is a duration and is already zone-independent; this clock
  // time is what makes the "when" align with the user's local time zone.
  const localResetTime = hasReset
    ? new Date(resetMs).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      })
    : null;

  return (
    <div className="om-limit" role="alert">
      <div className="om-limit__head">
        <span className="om-dot om-dot--err" />
        <span className="om-limit__title">
          {guest
            ? `You've used today's ${fmtTokens(limit)} free tokens.`
            : `You've reached today's ${fmtTokens(limit)} token limit.`}
        </span>
      </div>

      {isReset ? (
        <div className="om-limit__status om-limit__status--ready">
          <span className="om-dot om-dot--ok" />
          Your daily limit has reset &mdash; send a message to continue.
        </div>
      ) : remaining !== null ? (
        <div className="om-limit__status">
          <div
            className="om-limit__meter"
            role="progressbar"
            aria-label="Time until your daily limit resets"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(pct)}
          >
            <span className="om-limit__meter-fill" style={{ width: `${String(pct)}%` }} />
          </div>
          <span className="om-limit__time">
            resets in {formatRemaining(remaining)}
            {localResetTime !== null ? ` · ${localResetTime}` : ''}
          </span>
        </div>
      ) : (
        // resetAt was unparseable — still set the expectation, just vaguely.
        <div className="om-limit__status">
          <span className="om-limit__time">resets at the start of your next day</span>
        </div>
      )}

      {guest ? (
        <div className="om-limit__upsell">
          A free account gets you a bigger daily budget — plus documents, memory, and
          email/text delivery.
          <Link to="/auth/signup" className="om-limit__cta">
            Create Free Account
          </Link>
        </div>
      ) : SUBSCRIPTIONS_ENABLED ? (
        <div className="om-limit__upsell">
          Upgrade for a bigger daily token budget — Premium, Plus, and Pro plans give you
          more every day.
          <Link to="/pricing" className="om-limit__cta">
            View plans
          </Link>
        </div>
      ) : (
        <p className="om-limit__upsell">
          Subscriptions for higher daily limits are coming soon.
        </p>
      )}
    </div>
  );
}
